import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { Modal, Button } from "antd";
import {
  FileTextOutlined,
  PictureOutlined,
  FilePdfOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import useBodyScrollLock from "../../../../../hooks/useBodyScrollLock";
import {
  detectFileType,
  getFileTypeIcon,
  fetchPdfAsBlob,
  fetchImageAsBlob,
  ZOOM_LIMITS,
} from "../../../utils/fileViewerHelpers";
import "../shared/sharedModalStyles.scss";

/**
 * File Viewer Modal Component
 * Modern file viewer for images, PDFs, and text files with enhanced UI/UX
 *
 * @param {Object} props
 * @param {Object|null} props.viewingFile - File object being viewed {fileName, downloadUrl, fileType}
 * @param {Function} props.onClose - Callback when modal is closed
 */
const FileViewerModal = ({ viewingFile, onClose }) => {
  // Loading and error states
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  
  // PDF viewing state
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const pdfBlobUrlRef = useRef(null);
  
  // Image viewing state (using blob URL for authentication)
  const [imageBlobUrl, setImageBlobUrl] = useState(null);
  const imageBlobUrlRef = useRef(null);
  
  // Image zoom and pan state
  const [currentZoomLevel, setCurrentZoomLevel] = useState(1);
  const [imagePanPosition, setImagePanPosition] = useState({ x: 0, y: 0 });
  const [isImageDragging, setIsImageDragging] = useState(false);
  const [dragStartPosition, setDragStartPosition] = useState({ x: 0, y: 0 });
  const imageElementRef = useRef(null);

  // Detect file type using useMemo for performance
  const fileMetadata = useMemo(() => {
    if (!viewingFile) return null;
    
    const detectedFileType = detectFileType(viewingFile);
    return {
      ...detectedFileType,
      fileName: viewingFile?.fileName || "File",
      downloadUrl: viewingFile?.downloadUrl,
    };
  }, [viewingFile]);

  // Lock body scroll when modal is open
  useBodyScrollLock(!!viewingFile);

  // Cleanup blob URLs on unmount
  useEffect(() => {
    return () => {
      if (pdfBlobUrlRef.current) {
        URL.revokeObjectURL(pdfBlobUrlRef.current);
        pdfBlobUrlRef.current = null;
      }
      if (imageBlobUrlRef.current) {
        URL.revokeObjectURL(imageBlobUrlRef.current);
        imageBlobUrlRef.current = null;
      }
    };
  }, []);

  // Reset image zoom/pan when file changes
  useEffect(() => {
    setCurrentZoomLevel(1);
    setImagePanPosition({ x: 0, y: 0 });
  }, [viewingFile]);

  // Fetch PDF or Image as blob when viewingFile changes
  useEffect(() => {
    // Cleanup previous blob URLs
    if (pdfBlobUrlRef.current) {
      URL.revokeObjectURL(pdfBlobUrlRef.current);
      pdfBlobUrlRef.current = null;
    }
    if (imageBlobUrlRef.current) {
      URL.revokeObjectURL(imageBlobUrlRef.current);
      imageBlobUrlRef.current = null;
    }

    // Reset states
    setPdfBlobUrl(null);
    setImageBlobUrl(null);
    setHasError(false);
    setIsLoading(true);

    if (!viewingFile || !fileMetadata) {
      setIsLoading(false);
      return;
    }

    // For PDFs, fetch as blob to prevent auto-download
    if (fileMetadata.isPdf && viewingFile.downloadUrl) {
      fetchPdfAsBlob(viewingFile.downloadUrl)
        .then((pdfBlob) => {
          const newBlobUrl = URL.createObjectURL(pdfBlob);
          pdfBlobUrlRef.current = newBlobUrl;
          setPdfBlobUrl(newBlobUrl);
          setIsLoading(false);
          setHasError(false);
        })
        .catch(() => {
          setHasError(true);
          setIsLoading(false);
        });
    }
    // For images, fetch as blob to include authentication headers
    else if (fileMetadata.isImage && viewingFile.downloadUrl) {
      fetchImageAsBlob(viewingFile.downloadUrl)
        .then((imageBlob) => {
          const newBlobUrl = URL.createObjectURL(imageBlob);
          imageBlobUrlRef.current = newBlobUrl;
          setImageBlobUrl(newBlobUrl);
          setIsLoading(false);
          setHasError(false);
        })
        .catch(() => {
          setHasError(true);
          setIsLoading(false);
        });
    }
    // For text files, loading will be handled by onLoad/onError
    else {
      setIsLoading(false);
    }
  }, [viewingFile, fileMetadata]);

  // Image zoom handlers
  const handleZoomIn = useCallback(() => {
    setCurrentZoomLevel((previousZoom) =>
      Math.min(previousZoom + ZOOM_LIMITS.STEP, ZOOM_LIMITS.MAX)
    );
  }, []);

  const handleZoomOut = useCallback(() => {
    setCurrentZoomLevel((previousZoom) => {
      const newZoomLevel = Math.max(
        previousZoom - ZOOM_LIMITS.STEP,
        ZOOM_LIMITS.MIN
      );
      if (newZoomLevel <= 1) {
        setImagePanPosition({ x: 0, y: 0 });
      }
      return newZoomLevel;
    });
  }, []);

  const handleResetZoom = useCallback(() => {
    setCurrentZoomLevel(1);
    setImagePanPosition({ x: 0, y: 0 });
  }, []);

  // Image drag handlers
  const handleImageMouseDown = useCallback(
    (mouseEvent) => {
      if (currentZoomLevel > 1) {
        setIsImageDragging(true);
        setDragStartPosition({
          x: mouseEvent.clientX - imagePanPosition.x,
          y: mouseEvent.clientY - imagePanPosition.y,
        });
      }
    },
    [currentZoomLevel, imagePanPosition]
  );

  const handleImageMouseMove = useCallback(
    (mouseEvent) => {
      if (isImageDragging && currentZoomLevel > 1) {
        setImagePanPosition({
          x: mouseEvent.clientX - dragStartPosition.x,
          y: mouseEvent.clientY - dragStartPosition.y,
        });
      }
    },
    [isImageDragging, currentZoomLevel, dragStartPosition]
  );

  const handleImageMouseUp = useCallback(() => {
    setIsImageDragging(false);
  }, []);

  // Wheel zoom for images (Ctrl + Scroll)
  const handleImageWheelZoom = useCallback(
    (wheelEvent) => {
      if (fileMetadata?.isImage && wheelEvent.ctrlKey) {
        wheelEvent.preventDefault();
        const zoomDelta = wheelEvent.deltaY > 0 ? -ZOOM_LIMITS.WHEEL_STEP : ZOOM_LIMITS.WHEEL_STEP;
        setCurrentZoomLevel((previousZoom) => {
          const newZoomLevel = Math.max(
            ZOOM_LIMITS.MIN,
            Math.min(previousZoom + zoomDelta, ZOOM_LIMITS.MAX)
          );
          if (newZoomLevel <= 1) {
            setImagePanPosition({ x: 0, y: 0 });
          }
          return newZoomLevel;
        });
      }
    },
    [fileMetadata]
  );

  // Setup image interaction event listeners
  useEffect(() => {
    if (fileMetadata?.isImage) {
      window.addEventListener("mousemove", handleImageMouseMove);
      window.addEventListener("mouseup", handleImageMouseUp);
      window.addEventListener("wheel", handleImageWheelZoom, { passive: false });
      
      return () => {
        window.removeEventListener("mousemove", handleImageMouseMove);
        window.removeEventListener("mouseup", handleImageMouseUp);
        window.removeEventListener("wheel", handleImageWheelZoom);
      };
    }
  }, [fileMetadata, handleImageMouseMove, handleImageMouseUp, handleImageWheelZoom]);

  // Retry PDF fetch handler
  const handleRetryPdfFetch = useCallback(() => {
    if (!viewingFile?.downloadUrl) return;
    
    setHasError(false);
    setIsLoading(true);
    
    fetchPdfAsBlob(viewingFile.downloadUrl)
      .then((pdfBlob) => {
        const newBlobUrl = URL.createObjectURL(pdfBlob);
        if (pdfBlobUrlRef.current) {
          URL.revokeObjectURL(pdfBlobUrlRef.current);
        }
        pdfBlobUrlRef.current = newBlobUrl;
        setPdfBlobUrl(newBlobUrl);
        setIsLoading(false);
        setHasError(false);
      })
      .catch(() => {
        setHasError(true);
        setIsLoading(false);
      });
  }, [viewingFile]);

  // Retry image load handler
  const handleRetryImageLoad = useCallback(() => {
    if (!viewingFile?.downloadUrl) return;
    
    setHasError(false);
    setIsLoading(true);
    
    // Cleanup previous blob URL
    if (imageBlobUrlRef.current) {
      URL.revokeObjectURL(imageBlobUrlRef.current);
      imageBlobUrlRef.current = null;
    }
    
    fetchImageAsBlob(viewingFile.downloadUrl)
      .then((imageBlob) => {
        const newBlobUrl = URL.createObjectURL(imageBlob);
        imageBlobUrlRef.current = newBlobUrl;
        setImageBlobUrl(newBlobUrl);
        setIsLoading(false);
        setHasError(false);
      })
      .catch(() => {
        setHasError(true);
        setIsLoading(false);
      });
  }, [viewingFile]);

  // Retry text file load handler
  const handleRetryTextFileLoad = useCallback(() => {
    setHasError(false);
    setIsLoading(true);
    const textIframe = document.querySelector(".file-viewer-text-iframe");
    if (textIframe) {
      textIframe.src = viewingFile.downloadUrl;
    }
  }, [viewingFile]);

  // Render loading state
  const renderLoadingState = (loadingMessage = "Loading...") => (
    <div className="file-viewer-loading">
      <div className="file-viewer-loading-content">
        <div className="file-viewer-loading-spinner">
          <div className="spinner-ring"></div>
          <div className="spinner-ring"></div>
          <div className="spinner-ring"></div>
        </div>
        {loadingMessage && (
          <p className="file-viewer-loading-text">{loadingMessage}</p>
        )}
      </div>
    </div>
  );

  // Render error state
  const renderErrorState = (
    errorTitle,
    errorMessage,
    errorIcon = <FilePdfOutlined />,
    onRetryHandler = null
  ) => (
    <div className="file-viewer-error">
      <div className="file-viewer-error-content">
        <div className="file-viewer-error-icon-wrapper">
          {React.cloneElement(errorIcon, { className: "file-viewer-error-icon" })}
        </div>
        <h3>{errorTitle}</h3>
        <p>{errorMessage}</p>
        <div className="file-viewer-error-actions">
          {onRetryHandler && (
            <button
              onClick={onRetryHandler}
              className="file-viewer-action-button file-viewer-retry-button"
            >
              <ReloadOutlined /> Retry
            </button>
          )}
        </div>
      </div>
    </div>
  );

  // Render action toolbar
  const renderToolbar = () => {
    const toolbarActions = [];

    if (fileMetadata && !hasError && fileMetadata.isImage) {
      toolbarActions.push(
        <button
          key="zoom-out"
          onClick={handleZoomOut}
          disabled={currentZoomLevel <= ZOOM_LIMITS.MIN}
          className="file-viewer-toolbar-button"
          title="Zoom Out"
        >
          <ZoomOutOutlined />
        </button>,
        <span key="zoom-level" className="file-viewer-zoom-level">
          {Math.round(currentZoomLevel * 100)}%
        </span>,
        <button
          key="zoom-in"
          onClick={handleZoomIn}
          disabled={currentZoomLevel >= ZOOM_LIMITS.MAX}
          className="file-viewer-toolbar-button"
          title="Zoom In"
        >
          <ZoomInOutlined />
        </button>,
        <button
          key="reset"
          onClick={handleResetZoom}
          disabled={currentZoomLevel === 1}
          className="file-viewer-toolbar-button"
          title="Reset Zoom"
        >
          Reset
        </button>
      );
    }

    toolbarActions.push(
      <Button
        key="cancel"
        onClick={onClose}
        className="file-viewer-cancel-button"
      >
        Cancel
      </Button>
    );

    return <div className="file-viewer-toolbar">{toolbarActions}</div>;
  };

  // Render file content based on type
  const renderFileContent = () => {
    if (!fileMetadata || !viewingFile) return null;

    // Image viewer with zoom/pan
    if (fileMetadata.isImage) {
      if (isLoading || !imageBlobUrl) {
        return (
          <div className="file-viewer-image-container">
            {renderLoadingState("Loading image...")}
          </div>
        );
      }

      if (hasError) {
        return (
          <div className="file-viewer-image-container">
            {renderErrorState(
              "Unable to Load Image",
              "Image cannot be displayed. Please try again.",
              <PictureOutlined />,
              handleRetryImageLoad
            )}
          </div>
        );
      }

      return (
        <div
          className="file-viewer-image-container"
          onMouseDown={handleImageMouseDown}
          style={{
            cursor:
              currentZoomLevel > 1
                ? isImageDragging
                  ? "grabbing"
                  : "grab"
                : "default",
          }}
        >
          <div
            className="file-viewer-image-wrapper"
            style={{
              transform: `translate(${imagePanPosition.x}px, ${imagePanPosition.y}px) scale(${currentZoomLevel})`,
              transition: isImageDragging ? "none" : "transform 0.2s ease-out",
            }}
          >
            <img
              ref={imageElementRef}
              src={imageBlobUrl}
              alt={fileMetadata.fileName}
              className="file-viewer-image"
              onLoad={() => {
                setIsLoading(false);
                setHasError(false);
              }}
              onError={() => {
                setIsLoading(false);
                setHasError(true);
              }}
            />
          </div>
        </div>
      );
    }

    // PDF viewer
    if (fileMetadata.isPdf) {
      if (isLoading || !pdfBlobUrl) {
        return (
          <div className="file-viewer-pdf-container">
            {renderLoadingState("Loading PDF...")}
          </div>
        );
      }

      if (hasError) {
        return (
          <div className="file-viewer-pdf-container">
            {renderErrorState(
              "Unable to Load PDF",
              "PDF cannot be displayed in the browser. Please try again.",
              <FilePdfOutlined />,
              handleRetryPdfFetch
            )}
          </div>
        );
      }

      return (
        <div className="file-viewer-pdf-container">
          <iframe
            src={pdfBlobUrl}
            className="file-viewer-pdf-iframe"
            title={fileMetadata.fileName}
            onLoad={() => setIsLoading(false)}
            onError={() => {
              setHasError(true);
              setIsLoading(false);
            }}
          />
        </div>
      );
    }

    // Text file viewer
    if (fileMetadata.isText) {
      return (
        <div className="file-viewer-text-container">
          {isLoading && renderLoadingState("Loading text file...")}
          <iframe
            src={viewingFile.downloadUrl}
            className="file-viewer-text-iframe"
            title={fileMetadata.fileName}
            onLoad={() => setIsLoading(false)}
            onError={() => {
              setHasError(true);
              setIsLoading(false);
            }}
          />
          {hasError &&
            renderErrorState(
              "Unable to Load Text File",
              "Text file cannot be displayed. Please try again.",
              <FileTextOutlined />,
              handleRetryTextFileLoad
            )}
        </div>
      );
    }

    // Default: show preview not available message
    return (
      <div className="file-viewer-default">
        <div className="file-viewer-default-content">
          <div className="file-viewer-default-icon">
            {getFileTypeIcon(fileMetadata.type)}
          </div>
          <h3>Preview Not Available</h3>
          <p>This file type cannot be previewed in the browser.</p>
        </div>
      </div>
    );
  };

  if (!viewingFile || !fileMetadata) return null;

  return (
    <Modal
      title={
        <div className="file-viewer-modal-title">
          <span className="file-viewer-title-icon">
            {getFileTypeIcon(fileMetadata.type)}
          </span>
          <span className="file-viewer-title-text" title={fileMetadata.fileName}>
            {fileMetadata.fileName}
          </span>
          <span className="file-viewer-title-badge">
            {fileMetadata.type.toUpperCase()}
          </span>
        </div>
      }
      open={!!viewingFile}
      onCancel={onClose}
      footer={renderToolbar()}
      width={1200}
      style={{ top: 40 }}
      maskClosable={true}
      keyboard={true}
      getContainer={() => document.body}
      closable={false}
      className="rate-review-file-viewer-modal"
      styles={{
        body: {
          padding: 0,
          height: "calc(100vh - 160px)",
          minHeight: "600px",
          maxHeight: "calc(100vh - 160px)",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        },
        content: {
          maxHeight: "calc(100vh - 80px)",
          borderRadius: "20px",
        },
        header: {
          padding: "24px 32px",
          paddingRight: "32px",
          paddingLeft: "32px",
          width: "100%",
          maxWidth: "100%",
          borderBottom: "1px solid rgba(0, 0, 0, 0.08)",
          background: "linear-gradient(to bottom, #ffffff 0%, #fafafa 100%)",
          borderRadius: "20px 20px 0 0",
          boxSizing: "border-box",
        },
        footer: {
          padding: "20px 32px",
          borderTop: "1px solid rgba(0, 0, 0, 0.08)",
          background: "linear-gradient(to top, #ffffff 0%, #fafafa 100%)",
        },
      }}
    >
      <div className="file-viewer-content-wrapper">{renderFileContent()}</div>
    </Modal>
  );
};

export default FileViewerModal;
